import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Minus, RotateCcw, Mail, Send, Sparkles, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PLANS } from "@/lib/plans";

const SUPPORT_EMAIL = "evermin1999@gmail.com";

const UI = {
  en: {
    title: "EVERMIN Help", subtitle: "Support & guidance",
    welcome: "Hi! 👋 How can I help you with EVERMIN?",
    pick: "Choose a topic or type your question:",
    placeholder: "Type your question…",
    email: "Email Support", newChat: "Start a new chat", newChatShort: "New chat",
    fallback: "I can help with how EVERMIN works, plans & billing, accounts, tools, photo/doc help and privacy. For anything else, our team is happy to help by email.",
    open: "these", more: "Related pages:",
  },
  hi: {
    title: "EVERMIN मदद", subtitle: "सहायता और मार्गदर्शन",
    welcome: "नमस्ते! 👋 EVERMIN में मैं आपकी कैसे मदद करूं?",
    pick: "एक विषय चुनें या अपना सवाल लिखें:",
    placeholder: "अपना सवाल लिखें…",
    email: "ईमेल सहायता", newChat: "नई चैट शुरू करें", newChatShort: "नई चैट",
    fallback: "मैं EVERMIN कैसे काम करता है, प्लान व बिलिंग, खाता, टूल्स, फ़ोटो/दस्तावेज़ मदद और प्राइवेसी में मदद कर सकता हूं। बाकी किसी भी बात के लिए हमारी टीम ईमेल पर मदद करने में खुश है।",
    more: "संबंधित पेज:",
  },
};

// Curated knowledge base — uses EVERMIN's ACTUAL features, plans, prices and policies.
const KB = [
  {
    key: "how",
    label_en: "How does EVERMIN work?", label_hi: "EVERMIN कैसे काम करता है?",
    match: ["how", "work", "start", "what is", "काम", "कैसे", "शुरू"],
    a_en: "EVERMIN helps you understand, compare, decide and act on everyday problems. Just type your question in the box on the homepage. For current or time-sensitive things (prices, rates, news), it searches the live web and shows the real sources it used. You also get Compare Anything, Smart Calculators, How-To Guides, and Photo & Document help — all in English or हिंदी.",
    a_hi: "EVERMIN रोज़मर्रा की समस्याओं को समझने, तुलना करने, निर्णय लेने और कार्य करने में मदद करता है। बस होमपेज पर बॉक्स में अपना सवाल लिखें। मौजूदा/समय-संवेदनशील बातों (कीमतें, दरें, समाचार) के लिए यह लाइव वेब खोजता है और उपयोग किए गए असली स्रोत दिखाता है। साथ ही Compare, स्मार्ट कैलकुलेटर, हाउ-टू गाइड और फ़ोटो व दस्तावेज़ मदद — अंग्रेज़ी या हिंदी में।",
    links: [{ to: "/", label_en: "Ask a question", label_hi: "सवाल पूछें" }, { to: "/guides", label_en: "Browse guides", label_hi: "गाइड देखें" }],
  },
  {
    key: "plans",
    label_en: "Membership & Plans", label_hi: "सदस्यता और प्लान",
    match: ["plan", "membership", "upgrade", "limit", "प्लान", "सदस्यता", "अपग्रेड", "सीमा"],
    a_en: "There are four plans:\n• Free — ₹0 — 10 AI answers/day\n• Basic — ₹199/month — 30/day\n• Plus — ₹299/month — 60/day\n• Pro — ₹499/month — 100/day\nHigher plans give more daily AI answers and more saved items. You can upgrade or switch anytime.",
    a_hi: "चार प्लान हैं:\n• फ्री — ₹0 — रोज़ 10 AI उत्तर\n• बेसिक — ₹199/माह — रोज़ 30\n• प्लस — ₹299/माह — रोज़ 60\n• प्रो — ₹499/माह — रोज़ 100\nऊंचे प्लान में अधिक दैनिक AI उत्तर और अधिक सहेजे आइटम मिलते हैं। आप कभी भी अपग्रेड या बदल सकते हैं।",
    links: [{ to: "/membership", label_en: "See plans", label_hi: "प्लान देखें" }],
  },
  {
    key: "billing",
    label_en: "Payment & Billing", label_hi: "भुगतान और बिलिंग",
    match: ["pay", "payment", "billing", "refund", "cancel", "cashfree", "invoice", "भुगतान", "बिलिंग", "रिफंड", "रद्द"],
    a_en: "Payments are processed securely by Cashfree. Your membership activates as soon as payment is confirmed and lasts one month. It's a one-time charge per period — you're not auto-charged; to continue you renew by paying again. You can cancel anytime in Settings; your plan stays active until the period ends. Any applicable taxes are shown at checkout. Refunds follow our Refund & Cancellation Policy.",
    a_hi: "भुगतान Cashfree द्वारा सुरक्षित रूप से प्रोसेस होते हैं। भुगतान की पुष्टि होते ही सदस्यता सक्रिय हो जाती है और एक माह चलती है। यह प्रति अवधि एक-बार का शुल्क है — स्वतः शुल्क नहीं लिया जाता; जारी रखने के लिए दोबारा भुगतान कर नवीनीकरण करें। आप सेटिंग्स से कभी भी रद्द कर सकते हैं; प्लान अवधि के अंत तक सक्रिय रहता है। लागू कर चेकआउट पर दिखते हैं। रिफंड हमारी रिफंड व रद्दीकरण नीति के अनुसार।",
    links: [{ to: "/refund-policy", label_en: "Refund policy", label_hi: "रिफंड नीति" }, { to: "/terms", label_en: "Terms", label_hi: "शर्तें" }],
  },
  {
    key: "account",
    label_en: "Account & Login", label_hi: "खाता और लॉगिन",
    match: ["account", "login", "log in", "sign up", "signup", "password", "reset", "register", "खाता", "लॉगिन", "पासवर्ड", "साइन"],
    a_en: "Sign up with your email and a password, then log in anytime. Forgot your password? Use 'Forgot password' and we'll email you a secure reset link. Everything you save lives in My EVERMIN, and you can log out to end your session.",
    a_hi: "अपने ईमेल और पासवर्ड से साइन अप करें, फिर कभी भी लॉगिन करें। पासवर्ड भूल गए? 'पासवर्ड भूल गए' उपयोग करें और हम आपको सुरक्षित रीसेट लिंक ईमेल करेंगे। आपकी सहेजी हर चीज़ मेरा EVERMIN में रहती है, और सत्र समाप्त करने के लिए लॉग आउट करें।",
    links: [{ to: "/login", label_en: "Log in", label_hi: "लॉगिन" }, { to: "/signup", label_en: "Sign up", label_hi: "साइन अप" }],
  },
  {
    key: "tools",
    label_en: "Compare & Calculators", label_hi: "तुलना और कैलकुलेटर",
    match: ["compare", "calculator", "calculate", "emi", "budget", "loan", "तुलना", "कैलकुलेटर", "बजट", "लोन"],
    a_en: "Compare Anything puts two options side by side with clear pros, cons and a 'best for you' pick based on your needs. Smart Calculators help with budgets, EMIs/loans, rent-vs-buy and more — instant answers in ₹.",
    a_hi: "Compare Anything दो विकल्पों को साथ रखकर स्पष्ट फ़ायदे, नुकसान और आपकी ज़रूरत के अनुसार 'आपके लिए बेहतर' विकल्प देता है। स्मार्ट कैलकुलेटर बजट, EMI/लोन, रेंट-बनाम-खरीद आदि में तुरंत ₹ में मदद करते हैं।",
    links: [{ to: "/compare", label_en: "Compare", label_hi: "तुलना" }, { to: "/calculators", label_en: "Calculators", label_hi: "कैलकुलेटर" }],
  },
  {
    key: "docs",
    label_en: "Photo & Docs", label_hi: "फ़ोटो और दस्तावेज़",
    match: ["photo", "image", "document", "pdf", "bill", "scan", "upload", "camera", "फ़ोटो", "दस्तावेज़", "बिल", "अपलोड"],
    a_en: "Tap the camera or gallery button next to the chat box to add a photo or PDF (up to 10 MB), then ask about it — for example 'What is the total on this bill?' or 'Explain this document in simple words.' EVERMIN reads the image and answers.",
    a_hi: "चैट बॉक्स के पास कैमरा या गैलरी बटन दबाकर फ़ोटो या PDF (10 MB तक) जोड़ें, फिर उसके बारे में पूछें — जैसे 'इस बिल का कुल कितना है?' या 'इस दस्तावेज़ को आसान शब्दों में समझाओ।' EVERMIN इमेज पढ़कर उत्तर देता है।",
    links: [{ to: "/assistant", label_en: "Photo & Document help", label_hi: "फ़ोटो व दस्तावेज़ मदद" }],
  },
  {
    key: "privacy",
    label_en: "Privacy & Data", label_hi: "प्राइवेसी और डेटा",
    match: ["privacy", "data", "delete", "secure", "security", "card", "प्राइवेसी", "डेटा", "हटा", "सुरक्षा"],
    a_en: "We store your account, saved items and uploads — all private to you and never shared with other users. Payments go through Cashfree; we never store your raw card number, CVV or UPI PIN. You can delete saved items anytime, and request full account deletion by email.",
    a_hi: "हम आपका खाता, सहेजी चीज़ें और अपलोड संग्रहीत करते हैं — सब आपके लिए निजी, अन्य उपयोगकर्ताओं से कभी साझा नहीं। भुगतान Cashfree से होते हैं; हम आपका कच्चा कार्ड नंबर, CVV या UPI PIN कभी संग्रहीत नहीं करते। आप कभी भी सहेजी चीज़ें हटा सकते हैं, और ईमेल से पूरा खाता हटाने का अनुरोध कर सकते हैं।",
    links: [{ to: "/privacy", label_en: "Privacy policy", label_hi: "प्राइवेसी नीति" }],
  },
  {
    key: "other",
    label_en: "Something else", label_hi: "कुछ और",
    match: [],
    a_en: "No problem! For anything not covered here, our team is happy to help. Tap Email Support below and we'll get back to you.",
    a_hi: "कोई बात नहीं! यहां न मिलने वाली किसी भी बात के लिए हमारी टीम मदद को तैयार है। नीचे ईमेल सहायता दबाएं, हम आपसे संपर्क करेंगे।",
    links: [],
  },
];

function findTopic(text) {
  const q = (text || "").toLowerCase();
  for (const t of KB) {
    if (t.match.some((m) => q.includes(m))) return t;
  }
  return null;
}

export function SupportChat() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = UI[language] || UI.en;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "bot", text: t.welcome, quick: true }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const answerTopic = (topic) => {
    const label = language === "hi" ? topic.label_hi : topic.label_en;
    const a = language === "hi" ? topic.a_hi : topic.a_en;
    setMessages((prev) => [
      ...prev,
      { role: "user", text: label },
      { role: "bot", text: a, links: topic.links },
    ]);
  };

  const submit = (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q) return;
    setInput("");
    const topic = findTopic(q);
    if (topic) {
      setMessages((prev) => [...prev, { role: "user", text: q }]);
      const a = language === "hi" ? topic.a_hi : topic.a_en;
      setMessages((prev) => [...prev, { role: "bot", text: a, links: topic.links }]);
    } else {
      setMessages((prev) => [
        ...prev,
        { role: "user", text: q },
        { role: "bot", text: t.fallback, emailCta: true },
      ]);
    }
  };

  const newChat = () => setMessages([{ role: "bot", text: t.welcome, quick: true }]);

  const goLink = (to) => { setOpen(false); navigate(to); };

  // Smart handoff: pre-fill the user's questions, plan, account email and page.
  const isAuthed = user && typeof user === "object";
  const tier = isAuthed ? (user.membership_tier || "free") : null;
  const planLabel = tier ? `EVERMIN ${(PLANS[tier] || PLANS.free).name.en} (₹${(PLANS[tier] || PLANS.free).price}/mo)` : (language === "hi" ? "लॉग इन नहीं" : "Not logged in");
  const questions = messages.filter((m) => m.role === "user").map((m) => m.text);
  const path = typeof window !== "undefined" ? window.location.pathname : "/";

  const buildMailto = () => {
    const qBlock = questions.length ? questions.map((q) => `- ${q}`).join("\n") : (language === "hi" ? "- (कृपया अपनी समस्या यहां लिखें)" : "- (please describe your issue here)");
    const L = language === "hi"
      ? { hi: "नमस्ते EVERMIN टीम,", need: "मुझे इनमें मदद चाहिए:", details: "--- मेरी जानकारी (स्वतः भरी) ---", name: "नाम", acc: "खाता ईमेल", plan: "मौजूदा प्लान", page: "पेज", lang: "भाषा", foot: "(आप नीचे और जानकारी जोड़ सकते हैं।)", none: "अतिथि" }
      : { hi: "Hi EVERMIN team,", need: "I need help with the following:", details: "--- My details (auto-filled) ---", name: "Name", acc: "Account email", plan: "Current plan", page: "Page", lang: "Language", foot: "(You can add more details below.)", none: "Guest" };
    const body = [
      L.hi, "", L.need, qBlock, "", L.details,
      `${L.name}: ${isAuthed ? (user.name || L.none) : L.none}`,
      `${L.acc}: ${isAuthed ? (user.email || "-") : L.none}`,
      `${L.plan}: ${planLabel}`,
      `${L.page}: ${path}`,
      `${L.lang}: ${language.toUpperCase()}`,
      "", L.foot,
    ].join("\n");
    const subjBits = [`EVERMIN Support`, planLabel];
    if (questions.length) subjBits.push(questions[0].slice(0, 40));
    const subject = subjBits.join(" — ");
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const mailto = buildMailto();

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-fab"
          aria-label={t.title}
          style={{ bottom: "calc(1rem + var(--evermin-composer, 0px))" }}
          className="fixed right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-primary/30 transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="support-window"
          style={{ bottom: "calc(1rem + var(--evermin-composer, 0px))", maxHeight: "calc(100vh - 2rem - var(--evermin-composer, 0px))" }}
          className="fixed right-4 z-[60] flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 bg-slate-950 px-4 py-3 text-white">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/20 text-primary-foreground">
                <Sparkles className="h-4.5 w-4.5 text-sky-300" />
              </span>
              <div className="leading-tight">
                <p className="font-display text-sm font-bold">{t.title}</p>
                <p className="text-[11px] text-slate-400">{t.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={newChat} data-testid="support-new-chat" title={t.newChat} aria-label={t.newChat} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
                <RotateCcw className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setOpen(false)} data-testid="support-minimize" title={t.newChatShort} aria-label="Minimize" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
                <Minus className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setOpen(false)} data-testid="support-close" aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-3.5 py-4" data-testid="support-messages">
            {messages.map((m, i) => (
              <div key={i}>
                {m.role === "user" ? (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-slate-900 px-3.5 py-2 text-sm text-white">{m.text}</div>
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700">
                      <p className="whitespace-pre-line leading-relaxed">{m.text}</p>
                      {m.links && m.links.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {m.links.map((l) => (
                            <button key={l.to} onClick={() => goLink(l.to)} data-testid={`support-link-${l.to.replace(/\//g, "") || "home"}`} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20">
                              {language === "hi" ? l.label_hi : l.label_en}<ArrowRight className="h-3 w-3" />
                            </button>
                          ))}
                        </div>
                      )}
                      {m.emailCta && (
                        <a href={mailto} data-testid="support-email-inline" className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">
                          <Mail className="h-3.5 w-3.5" /> {t.email}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {m.quick && (
                  <div className="mt-3">
                    <p className="mb-2 px-1 text-xs font-medium text-slate-400">{t.pick}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {KB.map((topic) => (
                        <button
                          key={topic.key}
                          onClick={() => answerTopic(topic)}
                          data-testid={`support-quick-${topic.key}`}
                          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-primary/40 hover:text-primary"
                        >
                          {language === "hi" ? topic.label_hi : topic.label_en}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer: email support + input */}
          <div className="border-t border-slate-200 bg-white p-3">
            <a href={mailto} data-testid="support-email-btn" className="mb-2 flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2 text-sm font-medium text-slate-700 hover:border-primary/40 hover:text-primary">
              <Mail className="h-4 w-4" /> {t.email}
            </a>
            <form onSubmit={submit} className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="support-input"
                placeholder={t.placeholder}
                className="min-h-[42px] flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary/40 focus:outline-none"
              />
              <button type="submit" data-testid="support-send" disabled={!input.trim()} aria-label="Send" className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-opacity disabled:opacity-40">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
